import { create, fromBinary, toBinary } from '@bufbuild/protobuf';
import {
  startTransition,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { type Item, ItemsSchema, RequestSchema } from '../../gen/ltd/v1/ws_pb';
import { useAuth } from '../providers/AuthProvider';
import { useNotification } from '../providers/NotificationProvider';

const MAX_RETRIES = 10;
const TIMEOUT_UNIT = 1000;

export const useWebSocket = () => {
  const { isAuthenticated, setIsAuthenticated } = useAuth();
  const { notify } = useNotification();
  const [items, setItems] = useState<Item[] | null>(null);
  const retryCountRef = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);

  const disconnect = useCallback(() => {
    if (socketRef.current) {
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.close();
      socketRef.current = null;
    }
  }, []);

  const connect = useCallback(
    ({ isManual }: { isManual: boolean }) => {
      if (!isAuthenticated) {
        return;
      }
      if (isManual) {
        retryCountRef.current = 0;
      }
      if (retryCountRef.current >= MAX_RETRIES) {
        console.error('Max retries reached. Stopping reconnection.');
        return;
      }

      disconnect();

      const wsHost =
        process.env.NODE_ENV === 'production'
          ? window.location.host
          : 'localhost:8080';

      const ws = new WebSocket(`ws://${wsHost}/ws`);
      ws.binaryType = 'arraybuffer';
      socketRef.current = ws;

      ws.onclose = (_event: CloseEvent) => {
        setItems(null);
        retryCountRef.current += 1;
        const timeout = TIMEOUT_UNIT;
        console.warn(
          `Connection lost. Retrying in ${timeout} ms... (${retryCountRef.current}/${MAX_RETRIES})`,
        );
        setTimeout(() => connect({ isManual: false }), timeout);
      };
      ws.onopen = () => {
        notify('success', 'Connected to server.');
        retryCountRef.current = 0;
        const buffer = toBinary(
          RequestSchema,
          create(RequestSchema, {
            command: { case: 'read', value: {} },
          }),
        );
        ws.send(buffer);
      };
      ws.onmessage = (event: MessageEvent) => {
        if (event.data instanceof ArrayBuffer) {
          try {
            const uint8Array = new Uint8Array(event.data);
            const decodedData = fromBinary(ItemsSchema, uint8Array);
            setItems(decodedData.items);
          } catch (e) {
            console.error(`Failed to decode:', ${e}`);
          }
        }
      };
    },
    [isAuthenticated, notify, disconnect],
  );

  useLayoutEffect(() => {
    connect({ isManual: true });
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (
          !socketRef.current ||
          socketRef.current.readyState === WebSocket.CLOSED ||
          socketRef.current.readyState === WebSocket.CLOSING
        ) {
          connect({ isManual: true });
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [connect]);

  const handleLogin = useCallback(
    async (formData: FormData) => {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value.toString());
      });
      startTransition(async () => {
        const res = await fetch('/api/ldaplogin', {
          method: 'POST',
          body: params,
        });
        if (res.ok) {
          retryCountRef.current = 0;
          setIsAuthenticated(true);
          connect({ isManual: true });
        } else {
          setIsAuthenticated(false);
          notify('error', 'Login failed.');
        }
      });
    },
    [connect, notify, setIsAuthenticated],
  );

  const handleLogout = useCallback(async () => {
    startTransition(async () => {
      const res = await fetch('/api/logout', {
        method: 'POST',
      });
      if (res.ok) {
        setIsAuthenticated(false);
        setItems(null);
      } else {
        notify('error', 'Logout failed.');
      }
    });
  }, [notify, setIsAuthenticated]);

  const createItem = useCallback(
    (value: string) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        notify('error', 'WebSocket is not connected');
        return;
      }
      const request = create(RequestSchema, {
        command: {
          case: 'create',
          value: { value },
        },
      });
      const buffer = toBinary(RequestSchema, request);
      socketRef.current.send(buffer);
    },
    [notify],
  );

  const renameItem = useCallback(
    (item: Item, value: string) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        notify('error', 'WebSocket is not connected');
        return;
      }
      const request = create(RequestSchema, {
        command: {
          case: 'update',
          value: {
            id: item.id,
            value: value,
            todo: item.todo,
            dot: item.dot,
          },
        },
      });
      const buffer = toBinary(RequestSchema, request);
      socketRef.current.send(buffer);
      notify('success', `Renamed ${item.value} to ${value}.`);
    },
    [notify],
  );

  const toggleArchived = useCallback(
    (item: Item) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        notify('error', 'WebSocket is not connected');
        return;
      }
      const request = create(RequestSchema, {
        command: {
          case: 'update',
          value: {
            id: item.id,
            value: item.value,
            todo: !item.todo,
            dot: item.dot,
          },
        },
      });
      const buffer = toBinary(RequestSchema, request);
      socketRef.current.send(buffer);
      notify(
        'success',
        `${item.todo ? 'Archived' : 'Unarchived'} ${item.value}.`,
      );
    },
    [notify],
  );

  const toggleDot = useCallback(
    (item: Item) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        notify('error', 'WebSocket is not connected');
        return;
      }
      const request = create(RequestSchema, {
        command: {
          case: 'update',
          value: {
            id: item.id,
            value: item.value,
            todo: item.todo,
            dot: item.dot === 3 ? 0 : item.dot + 1,
          },
        },
      });
      const buffer = toBinary(RequestSchema, request);
      socketRef.current.send(buffer);
    },
    [notify],
  );

  const sort = useCallback(
    (target: string, insert: string, last: boolean) => {
      if (
        !socketRef.current ||
        socketRef.current.readyState !== WebSocket.OPEN
      ) {
        notify('error', 'WebSocket is not connected');
        return;
      }
      const request = create(RequestSchema, {
        command: {
          case: 'sort',
          value: {
            target,
            insert,
            last,
          },
        },
      });
      const buffer = toBinary(RequestSchema, request);
      socketRef.current.send(buffer);
    },
    [notify],
  );

  const deleteArchived = useCallback(() => {
    if (!socketRef.current || socketRef.current.readyState !== WebSocket.OPEN) {
      notify('error', 'WebSocket is not connected');
      return;
    }
    const request = create(RequestSchema, {
      command: {
        case: 'delete',
        value: {},
      },
    });
    const buffer = toBinary(RequestSchema, request);
    socketRef.current.send(buffer);
    notify('success', 'Deleted archived items.');
  }, [notify]);

  return {
    items,
    handleLogin,
    handleLogout,
    createItem,
    renameItem,
    toggleArchived,
    toggleDot,
    sort,
    deleteArchived,
  };
};
